import React, { Suspense, lazy } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { FallbackView } from '../../_metronic/partials'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import AccountWrapper from "../pages/accounts/AccountWrapper";
import GrantAccessWrapper from "../pages/accounts/GrantAccessWrapper";
import StoreWrapper from "../pages/store/StoreWrapper";
import { IsSentoAdmin, usePermissionChecker } from '../../app/hooks/PermissionChecker'
import { LIST_PERMISSIONS } from '../../app/constants/common'
import SupplierWrapper from '../pages/suppliers/SupplierWrapper'
import AbilitiesWrapper from '../pages/abilities/AbilitiesWrapper'
import ReceiptVoucherWrapper from '../pages/abilities/receipt-voucher/ReceiptVoucherWrapper'
import PaymentVoucherWrapper from '../pages/abilities/agency/PaymentVoucherWrapper'
import AgencyDebtWrapper from '../pages/debt/agency/AgencyDebtWrapper'
import SupplierDebtWrapper from '../pages/debt/supplier/SupplierDebtWrapper'
import SalesReportWrapper from '../pages/debt/SalesReportWrapper'
import DetailSearchWrapper from '../pages/order/detail-search/DetailSearchWrapper'
import InventoryDeliveryWrapper from '../pages/warehouse/InventoryDelivery/InventoryDeliveryWrapper'
import InventoryDeliveryDetailWrapper from '../pages/warehouse/InventoryDelivery/detail/InventoryDeliveryDetailWrapper'
import ReceiptProductsWrapper from '../pages/warehouse/receipt/detail-search/ReceiptProductsWrapper'
import InventoryDeliveryProductsWrapper from '../pages/warehouse/InventoryDelivery/detail-search/InventoryDeliveryProductsWrapper'
import SalesProductWrapper from '../pages/add-sales-products/SalesProductWrapper'
import AddProductsWrapper from '../pages/add-sales-products/create/AddProductsWrapper'
import AccountInfoWrapper from '../pages/personnel-info/AccountInfoWrapper'
import AddCartWrapper from '../pages/cart/CartWrapper'
import ImportRequestWrapper from '../pages/warehouse/import-request/ImportRequestWrapper'
import CreateImportRequestWrapper from '../pages/warehouse/import-request/create/CreateImportRequestWrapper'
import ViewImportRequestWrapper from '../pages/warehouse/import-request/detail/ViewImportRequestWrapper'
import PaymentRequestWrapper from '../pages/abilities/payment-request/PaymentRequestWrapper';
import ConsignmentSupplierWrapper from '../pages/suppliers-consignment/ConsignmentSupplierWrapper';
import ConsignmentSalesWrapper from '../pages/consignment-sales/ConsignmentSalesWrapper';
import AccountBooksWrapper from '../pages/account-books/AccountBooksWrapper';
import TaxGroupWrapper from '../pages/tax-groups/TaxGroupWrapper';

export function PrivateRoutes() {
  const { hasPermission } = usePermissionChecker();
  const ProductsWrapper = lazy(() => import('../pages/products/ProductsWrapper'))
  const AgentWrapper = lazy(() => import('../pages/agent/AgentWrapper'))
  const AgentDetailWrapper = lazy(() => import('../pages/agent-detail/AgentDetailWrapper'))
  const AgentProductWrapper = lazy(() => import('../pages/agent-detail/product/AgentProductWrapper'))
  const OrderWrapper = lazy(() => import('../pages/order/OrderWrapper'))
  const OrderDetailWrapper = lazy(() => import('../pages/order/detail/OrderDetailWrapper'))
  const WareHouseWrapper = lazy(() => import('../pages/warehouse/WareHouseWrapper'))
  const ReceiptWrapper = lazy(() => import('../pages/warehouse/receipt/ReceiptWrapper'))
  const ReceiptDetailWrapper = lazy(() => import('../pages/warehouse/receipt/detail/ReceiptDetailWrapper'))
  const CartWrapper = lazy(() => import('../pages/warehouse/cart/CartWrapper'))
  const InventoryCheckSheetWrapper = lazy(() => import('../pages/warehouse/inventory-check-sheet/InventoryCheckSheetWrapper'))
  const InventoryCheckSheetDetailWrapper = lazy(() => import('../pages/warehouse/inventory-check-sheet/detail/InventoryCheckSheetDetailWrapper'))
  const WarrantyWrapper = lazy(() => import('../pages/warehouse/warranty/WarrantyWrapper'))
  const WarrantyDetailWrapper = lazy(() => import('../pages/warehouse/warranty/detail/WarrantyDetailWrapper'))
  const LogHistWrapper = lazy(() => import('../pages/login-histtory/LogHistWrapper'))
  const ComboWrapper = lazy(() => import('../pages/products-combo/ComboWrapper'))
  const ProductSaleWrapper = lazy(() => import('../pages/order/product-sale-report/ProductSaleWrapper'))
  const ReceiptCreateByImgWrapper = lazy(() => import('../pages/warehouse/receipt/create-by-image/ReceiptCreateByImgWrapper'))
  const ConsignmentWrapper = lazy(() => import('../pages/products-consignment/ConsignmentWrapper'))
  const ImportOpeningStockWrapper = lazy(() => import('../pages/warehouse/import-opening-stock/ImportOpeningStockWrapper'))
  const ReconcileWrapper = lazy(() => import('../pages/reconcile/ReconcileWrapper'))
  const BankReconcileWrapper = lazy(() => import('../pages/reconcile-bank/BankReconcileWrapper'))
  const BulkImportInvoiceWrapper = lazy(() => import('../pages/order/bulk-import-invoices/BulkImportInvoiceWrapper'))
  const BulkImportReceiptInvoiceWrapper = lazy(() => import('../pages/warehouse/receipt/bulk-import-invoices/BulkImportInvoiceWrapper'))
  const AccountanceUserWrapper = lazy(() => import('../pages/accountance-users/AccountanceUsersWrapper'))
  const ProductHubWrapper = lazy(() => import('../pages/product-hub/ProductHubWrapper'))
  const TempProductHubWrapper = lazy(() => import('../pages/product-hub-temp/TempProductHubWrapper'))

  const renderRoute = (path: string, component: any, requiredPermission?: string) => {
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return <Redirect to="/error/403" />;
    }
    return <Route exact path={path} component={component} />;
  };

  const renderAdminRoute = (path: string, component: any) => {
    if (!IsSentoAdmin()) {
      return <Redirect to="/error/403" />;
    }
    return <Route exact path={path} component={component} />;
  };

  const protectedRoutes = [
    { path: '/accounts/list', component: AccountWrapper, permission: LIST_PERMISSIONS.ACCOUNTS },
    { path: '/account/add', component: AccountWrapper, permission: LIST_PERMISSIONS.ACCOUNTS },
    { path: '/account/edit/:id', component: AccountWrapper, permission: LIST_PERMISSIONS.ACCOUNTS },
    { path: '/accounts/roles', component: GrantAccessWrapper, permission: LIST_PERMISSIONS.ACCOUNTS },
    { path: '/stores', component: StoreWrapper, permission: LIST_PERMISSIONS.STORES },
    { path: '/debt/agency', component: AgencyDebtWrapper, permission: LIST_PERMISSIONS.AGENT_DEBT},
    { path: '/debt/supplier', component: SupplierDebtWrapper, permission: LIST_PERMISSIONS.SUPPLIER_DEBT},
    { path: '/sales-report', component: SalesReportWrapper, permission: LIST_PERMISSIONS.SALES},

    { path: '/products/simple', component: ProductsWrapper, permission: LIST_PERMISSIONS.PRODUCTS},
    { path: '/products/combo', component: ComboWrapper, permission: LIST_PERMISSIONS.COMBO },
    { path: '/products/consignment', component: ConsignmentWrapper, permission: LIST_PERMISSIONS.CONSIGN_PRODUCTS},
    { path: '/products/combo/add', component: ComboWrapper, permission: LIST_PERMISSIONS.COMBO },
    { path: '/products/combo/view/:id?', component: ComboWrapper, permission: LIST_PERMISSIONS.COMBO },
    { path: '/products/detail', component: ProductsWrapper, permission: LIST_PERMISSIONS.PRODUCTS },
    { path: '/products/detail/:id?', component: ProductsWrapper, permission: LIST_PERMISSIONS.PRODUCTS },
    { path: '/sales-products', component: SalesProductWrapper, permission: LIST_PERMISSIONS.PRODUCTS },
    { path: '/sales-products/add', component: AddProductsWrapper, permission: LIST_PERMISSIONS.PRODUCTS },

    { path: '/agents', component: AgentWrapper, permission: LIST_PERMISSIONS.AGENTS },
    { path: '/agents/detail/:id?', component: AgentDetailWrapper, permission: LIST_PERMISSIONS.AGENTS },
    { path: '/agents/detail/:id/product', component: AgentProductWrapper, permission: LIST_PERMISSIONS.AGENTS },

    { path: '/orders', component: OrderWrapper, permission: LIST_PERMISSIONS.ORDERS },
    { path: '/orders/detail/:id?', component: OrderDetailWrapper, permission: LIST_PERMISSIONS.ORDERS },
    { path: '/add-to-cart/:id?', component: AddCartWrapper, permission: LIST_PERMISSIONS.ORDERS },
    { path: '/orders/product-search', component: DetailSearchWrapper, permission: LIST_PERMISSIONS.PURCHASED_SEARCH },
    { path: '/orders/product-sale', component: ProductSaleWrapper, permission: LIST_PERMISSIONS.PRODUCT_SALES_STATISTIC },
    { path: '/orders/bulk-import-invoices', component: BulkImportInvoiceWrapper, permission: LIST_PERMISSIONS.ORDERS },

    { path: '/warehouse/list', component: WareHouseWrapper, permission: LIST_PERMISSIONS.WAREHOUSE },
    { path: '/warehouse/receipt/list', component: ReceiptWrapper, permission: LIST_PERMISSIONS.RECEIPT_IN },
    { path: '/warehouse/receipt/create-by-image', component: ReceiptCreateByImgWrapper, permission: LIST_PERMISSIONS.CREATE_RECEIPT_BY_IMG },
    { path: '/warehouse/receipt/detail/:id?', component: ReceiptDetailWrapper, permission: LIST_PERMISSIONS.RECEIPT_IN },
    { path: '/warehouse/receipt/find-products', component: ReceiptProductsWrapper, permission: LIST_PERMISSIONS.FIND_PD_IN_RECEIPTS },
    { path: '/warehouse/inventory-delivery/list', component: InventoryDeliveryWrapper, permission: LIST_PERMISSIONS.RECEIPT_OUT },
    { path: '/warehouse/inventory-delivery/detail/:id?', component: InventoryDeliveryDetailWrapper, permission: LIST_PERMISSIONS.RECEIPT_OUT },
    { path: '/warehouse/inventory-delivery/find-products', component: InventoryDeliveryProductsWrapper, permission: LIST_PERMISSIONS.RECEIPT_OUT },
    { path: '/warehouse/cart', component: CartWrapper, permission: LIST_PERMISSIONS.CARTS },
    { path: '/warehouse/inventory-check-sheet', component: InventoryCheckSheetWrapper, permission: LIST_PERMISSIONS.WAREHOUSE },
    { path: '/warehouse/inventory-check-sheet/detail/:id?', component: InventoryCheckSheetDetailWrapper, permission: LIST_PERMISSIONS.WAREHOUSE },
    { path: '/warehouse/warranty_refund', component: WarrantyWrapper, permission: LIST_PERMISSIONS.WARRANTY_REFUND },
    { path: '/warehouse/warranty_refund/detail/:id?', component: WarrantyDetailWrapper, permission: LIST_PERMISSIONS.WARRANTY_REFUND },
    { path: '/warehouse/import-opening-stocks', component: ImportOpeningStockWrapper, permission: LIST_PERMISSIONS.WAREHOUSE },
    { path: '/warehouse/receipt/bulk-import-invoices', component: BulkImportReceiptInvoiceWrapper, permission: LIST_PERMISSIONS.CREATE_RECEIPT_BY_IMG },
    
    { path: '/warehouse/receipt/import-requests', component: ImportRequestWrapper, permission: LIST_PERMISSIONS.IMPORT_REQUEST },
    { path: '/warehouse/receipt/import-requests/add', component: CreateImportRequestWrapper, permission: LIST_PERMISSIONS.IMPORT_REQUEST },
    { path: '/warehouse/receipt/import-requests/:id', component: ViewImportRequestWrapper, permission: LIST_PERMISSIONS.IMPORT_REQUEST },
    
    { path: '/abilities/index', component: AbilitiesWrapper, permission: LIST_PERMISSIONS.CASH_BANK_BOOK },
    { path: '/abilities/receipt-voucher', component: ReceiptVoucherWrapper, permission: LIST_PERMISSIONS.RECIEPT_VOUCHER },
    { path: '/abilities/payment-voucher', component: PaymentVoucherWrapper, permission: LIST_PERMISSIONS.PAYMENT_VOUCHER },
    { path: '/abilities/payment-request', component: PaymentRequestWrapper, permission: LIST_PERMISSIONS.PAYMENT_REQUEST },

    { path: '/suppliers', component: SupplierWrapper, permission: LIST_PERMISSIONS.SUPPLIERS },

    { path: '/account-books/reconcile', component: ReconcileWrapper, permission: LIST_PERMISSIONS.ACCOUNT_BOOKS },
    { path: '/account-books/bank-reconcile', component: BankReconcileWrapper, permission: LIST_PERMISSIONS.ACCOUNT_BOOKS },
    { path: '/account-books/index', component: AccountBooksWrapper, permission: LIST_PERMISSIONS.ACCOUNT_BOOKS },
  ];

  const normalRoutes = [
    { path: '/', component: DashboardWrapper },
    { path: '/dashboard', component: DashboardWrapper },
    { path: '/auth-logs', component: LogHistWrapper },
    { path: '/my-account', component: AccountInfoWrapper },
    //{ path: '/printers', component: PrinterWrapper},
  ];

  const superAdminRoutes = [
    { path: '/consigment-suppliers', component: ConsignmentSupplierWrapper },
    { path: '/consigment-sales', component: ConsignmentSalesWrapper },
    { path: '/account-books/mst-tax-groups', component: TaxGroupWrapper },
    { path: '/accountance-users', component: AccountanceUserWrapper },
    { path: '/products-hub/approved', component: ProductHubWrapper },
    { path: '/products-hub/temp', component: TempProductHubWrapper },
  ];

  return (
    <Suspense fallback={<FallbackView />}>
      <Switch>
        {protectedRoutes.map((route) => (
          <Route exact key={route.path} path={route.path}>
            {renderRoute(route.path, route.component, route.permission)}
          </Route>
        ))}
        {superAdminRoutes.map((route) => (
          <Route exact key={route.path} path={route.path}>
            {renderAdminRoute(route.path, route.component)}
          </Route>
        ))}
        {normalRoutes.map((route) => (
          <Route exact key={route.path} path={route.path} component={route.component} />
        ))}
        {/*<Redirect from='/' to='/dashboard' />*/}
      </Switch>
    </Suspense>
  );
}
